import {
  ContactTypes,
  IContactRespSchema,
  IQuoteRequestSchema,
  QuoteStatusEnum,
  SigningOrderEnum,
} from '@monetize/types/app';
import { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useUpdateQuoteEsignRequirement } from '../../../../api/cpqService';
import {
  MBox,
  MFlex,
  MSwitch,
  QuoteSelectInput,
} from '../../../../components/Monetize';
import { MCustomSelect } from '../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { QuoteBasicContactSelect } from '../../../../components/Quotes/QuoteBasicContactSelect';
import { QuoteBasicInfoLine } from '../../../../components/Quotes/QuoteBasicInfoLine';
import { useIsTenantEsignConfigured } from '../../../../hooks/useIsTenantEsignConfigured';
import { renderItemContentForAccount } from '../../../Settings/rules-v3/rules.utils';
import { useQuoteContactsDrawer } from '../../Quote/components/contacts/quoteContactsDrawerContext';
import { useQuoteContext } from '../../Quote/quoteContext';
import {
  getCanChangeLinkedAccount,
  shouldAllowEsignEdit,
} from '../../Quote/quoteUtils';

export const QuoteBasicContacts = () => {
  const {
    quoteContacts: {
      quoteContacts,
      internalAccountContacts,
      handleChangeContact,
      isDisabledEsign,
      isDisabledPrimaryBilling,
      linkedAccount,
      handleChangeBillingAccount,
      billingAccountId,
      accountSelectItems,
      esignCustomerAccountId,
      shippingAccountId,
      handleChangeShippingAccountId,
    },
    quoteData: { quote },
    quoteSettingsData: { quoteSettings },
    isReadOnlyQuote,
  } = useQuoteContext();
  const isTenantEsignConfigured = useIsTenantEsignConfigured();
  const { openContactsDrawer } = useQuoteContactsDrawer();

  const handleOpenEsignSettings = useCallback(
    () => openContactsDrawer('esign'),
    [openContactsDrawer],
  );

  const handleOpenContacts = useCallback(
    () => openContactsDrawer('contacts'),
    [openContactsDrawer],
  );

  const esignContact = quoteContacts.esign.find((contact) => !contact.internal);
  const esignInternalContact = quoteContacts.esign.find(
    (contact) => contact.internal,
  );
  const { allowEsignEditOnQuoteReview } = quoteSettings || {};

  const { control, watch } = useFormContext<IQuoteRequestSchema>();
  const requiresEsignWatch = watch('requiresEsign');

  const canChangeLinkedAccount = getCanChangeLinkedAccount(quote!);

  const updateEsignRequirement = useUpdateQuoteEsignRequirement();

  const handleChangeContactAndEvaluate = async (
    contact: IContactRespSchema,
    type: ContactTypes,
    isInternal: boolean,
  ) => {
    handleChangeContact(contact, type, isInternal);
  };

  const isEsignControlReadOnlyOrDisabled = () => {
    return shouldAllowEsignEdit(quote?.status, allowEsignEditOnQuoteReview)
      ? false
      : isReadOnlyQuote || quote?.status !== QuoteStatusEnum.DRAFT;
  };

  return (
    <MBox>
      {linkedAccount && (
        <QuoteBasicInfoLine
          title="Shipping Account"
          inputId="quote-header-shipping-account"
        >
          <MBox mr={!canChangeLinkedAccount ? 0 : '-2'}>
            <MCustomSelect
              itemTitle="accountName"
              itemValue="id"
              MainInputComponent={QuoteSelectInput}
              items={accountSelectItems}
              renderItemContent={renderItemContentForAccount}
              value={shippingAccountId}
              onChange={handleChangeShippingAccountId}
              isSelectOpen={false}
              inputProps={{
                autoComplete: 'off',
              }}
              placeholder="Select"
              showQueryInput
              showContentInInput
              popOverProps={{
                positioning: { placement: 'bottom-end', sameWidth: false },
              }}
              skipFilter={true}
              isLazy
              useMainInputAsSearchInput={false}
              clearable={false}
              isDisabled={!canChangeLinkedAccount}
            />
          </MBox>
        </QuoteBasicInfoLine>
      )}

      <QuoteBasicInfoLine
        title="Shipping Contact"
        onTitleClick={handleOpenContacts}
        titleTooltip="Manage contacts"
        showRequired={!quoteContacts.primary}
      >
        <MBox mr={isDisabledPrimaryBilling ? 0 : '-2'}>
          <QuoteBasicContactSelect
            value={quoteContacts.primary}
            onChange={(contact) =>
              handleChangeContactAndEvaluate(contact, 'primary', false)
            }
            id="quote-header-primary-contact"
            aria-label="Shipping Contact"
            isDisabled={isDisabledPrimaryBilling}
            isReadOnly={isDisabledPrimaryBilling}
            accountId={shippingAccountId!}
          />
        </MBox>
      </QuoteBasicInfoLine>

      {linkedAccount && (
        <QuoteBasicInfoLine
          title="Billing Account"
          inputId="quote-header-billing-account"
        >
          <MBox mr={!canChangeLinkedAccount ? 0 : '-2'}>
            <MCustomSelect
              itemTitle="accountName"
              itemValue="id"
              MainInputComponent={QuoteSelectInput}
              items={accountSelectItems}
              renderItemContent={renderItemContentForAccount}
              value={billingAccountId}
              onChange={handleChangeBillingAccount}
              isSelectOpen={false}
              inputProps={{
                autoComplete: 'off',
              }}
              placeholder="Select"
              showQueryInput
              showContentInInput
              popOverProps={{
                positioning: { placement: 'bottom-end', sameWidth: false },
              }}
              skipFilter={true}
              isLazy
              useMainInputAsSearchInput={false}
              clearable={false}
              isDisabled={!canChangeLinkedAccount}
            />
          </MBox>
        </QuoteBasicInfoLine>
      )}

      <QuoteBasicInfoLine
        title="Billing Contact"
        onTitleClick={handleOpenContacts}
        titleTooltip="Manage contacts"
        showRequired={!quoteContacts.billing}
        fieldKey="contacts.billing"
      >
        <MBox mr={isDisabledPrimaryBilling ? 0 : '-2'}>
          <QuoteBasicContactSelect
            value={quoteContacts.billing}
            onChange={(contact) =>
              handleChangeContactAndEvaluate(contact, 'billing', false)
            }
            id="quote-header-billing-contact"
            aria-label="Billing Contact"
            isDisabled={isDisabledPrimaryBilling}
            isReadOnly={isDisabledPrimaryBilling}
            accountId={billingAccountId!}
          />
        </MBox>
      </QuoteBasicInfoLine>

      {isTenantEsignConfigured && (
        <QuoteBasicInfoLine
          title="eSign"
          onTitleClick={handleOpenEsignSettings}
          titleTooltip="Configure additional eSign settings"
          fieldKey="requiresEsign"
        >
          <MFlex>
            <Controller
              name="requiresEsign"
              control={control}
              render={({ field: { value, onChange, ...rest } }) => (
                <MSwitch
                  isReadOnly={isEsignControlReadOnlyOrDisabled()}
                  isDisabled={isEsignControlReadOnlyOrDisabled()}
                  isChecked={value}
                  // The row title is a button that opens the contacts
                  // drawer, so it cannot double as this switch's label.
                  aria-label="Requires eSign"
                  onChange={(e) => {
                    onChange(e.target.checked);
                    updateEsignRequirement.mutate({
                      quoteId: quote?.id ?? '',
                      requiresEsign: e.target.checked,
                    });
                  }}
                  {...rest}
                />
              )}
            />
          </MFlex>
        </QuoteBasicInfoLine>
      )}
      {isTenantEsignConfigured && requiresEsignWatch && (
        <>
          {quote?.signingOrder !== SigningOrderEnum.EXTERNAL_ONLY && (
            <QuoteBasicInfoLine
              title="eSign Internal Contact"
              inputId="quote-header-esign-internal-contact"
              showRequired={!esignContact}
            >
              <MBox mr={isDisabledEsign ? 0 : '-2'}>
                <QuoteBasicContactSelect
                  id="quote-header-esign-internal-contact"
                  value={esignInternalContact}
                  onChange={(contact) =>
                    handleChangeContactAndEvaluate(contact, 'esign', true)
                  }
                  contacts={internalAccountContacts}
                  isDisabled={isDisabledEsign}
                  isReadOnly={isDisabledEsign}
                  accountId={quote?.accountId}
                  defaultValues={{ internal: true, esigner: true }}
                />
              </MBox>
            </QuoteBasicInfoLine>
          )}
          <QuoteBasicInfoLine
            title="eSign Customer Contact"
            inputId="quote-header-esign-customer-contact"
            showRequired={!esignContact}
          >
            <MBox mr={isDisabledEsign ? 0 : '-2'}>
              <QuoteBasicContactSelect
                id="quote-header-esign-customer-contact"
                value={esignContact}
                onChange={(contact) =>
                  handleChangeContactAndEvaluate(contact, 'esign', false)
                }
                isDisabled={isDisabledEsign}
                isReadOnly={isDisabledEsign}
                accountId={
                  esignCustomerAccountId === quote?.accountId
                    ? quote?.accountId
                    : esignCustomerAccountId!
                }
              />
            </MBox>
          </QuoteBasicInfoLine>
        </>
      )}
    </MBox>
  );
};
